<template>
  <div class="view-gabarit admin">
    <div class="content">
      <div class="header-container">
        <div class="title">{{ $t('studyCommuter') }}</div>
        <div class="search-bar" :class="{ remote: !isDesktop }">
          <i class="icon-glass"></i>
          <input v-model.trim="search" class="search-input" type="text" :placeholder="$t('searchText')" @keyup.enter="searchStudies" />
          <div v-if="!isDesktop" class="search-button" @click="searchStudies">{{ $t('search') }}</div>
        </div>
        <div v-if="userInfo.role_id === 0 || userInfo.role_id === 1" class="study-button" @click="() => newStudy()">
          <span class="icon-sizer"><i class="icon-plus"></i></span>
          {{ $t('createANewStudy') }}
        </div>
      </div>
      <div class="study-counter">
        <div v-if="fetching"></div>
        <span v-else>
          {{ $t('study', searchedStudies.length, { count: searchedFullStudies.length }) }}
        </span>
      </div>
    </div>

    <div class="study-list" :class="{ empty: searchedStudies.length <= 0 && isDesktop, 'has-footer': userInfo.role_id !== 3 && isDesktop }">
      <div v-if="isDuplicating" class="loader-holder">
        <Loader />
      </div>

      <div v-if="fetching" class="loader-holder">
        <Loader />
      </div>
      <div v-else-if="searchedStudies.length > 0" class="card-container">
        <div class="content">
          <Card v-for="study in searchedStudies.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize)" :key="study.id" :data="study" />
        </div>
      </div>
      <div v-else class="no-study">
        {{ $t('noStudyNow') }}
      </div>

      <div
        v-if="!isDesktop && !fetching && (searchedStudies.length === 0 || studies.length !== searchedStudies.length)"
        class="showall-button"
        @click="showAllresults"
      >
        {{
          $t('remoteSeeStudy', searchedFullStudies.length - searchedStudies.length, {
            count: searchedFullStudies.length - searchedStudies.length,
          })
        }}
      </div>
      <div v-if="!isDesktop && !fetching && searchedStudies.length > 0" class="hideall-button" @click="hideAllresults">
        {{ $t('remoteHideStudy') }}
      </div>
    </div>

    <div class="pagination">
      <div class="content">
        <div class="prev" :class="{ disabled: pageIndex <= 0 }" @click="pageIndex--">
          {{ $t('previous') }}
        </div>
        <div class="number" :class="{ active: pageIndex === 0 }" @click="pageIndex = 0">1</div>
        <div v-if="pageIndex > 2" class="number disabled">...</div>
        <div
          v-for="i in [pageIndex - 1, pageIndex, pageIndex + 1].filter(e => e > 0 && e < Math.floor(searchedStudies.length / pageSize))"
          :key="i"
          class="number"
          :class="{ active: i === pageIndex }"
          @click="pageIndex = i"
        >
          {{ i + 1 }}
        </div>
        <div v-if="pageIndex < Math.floor(searchedStudies.length / pageSize) - 2" class="number disabled">...</div>
        <div
          class="number"
          :class="{ active: pageIndex === Math.floor(searchedStudies.length / pageSize) }"
          @click="pageIndex = Math.floor(searchedStudies.length / pageSize)"
        >
          {{ Math.ceil(searchedStudies.length / pageSize) }}
        </div>
        <div class="next" :class="{ disabled: pageIndex >= Math.floor(searchedStudies.length / pageSize) }" @click="pageIndex++">
          {{ $t('next') }}
        </div>
      </div>
    </div>

    <div v-if="userInfo.role_id !== 3 && isDesktop" class="footer">
      <div class="footer-item">
        <span class="footer-icon">
          <i class="icon-csv"></i>
        </span>
        <div class="title">{{ $t('csvTemplate') }}</div>
        <div class="text">{{ $t('csvTemplateDetail') }}</div>
        <button class="download" @click="downloadCSV()">
          {{ $t('download') }}
        </button>
      </div>
      <div class="horizontal-divider"></div>
      <div class="footer-item">
        <span class="footer-icon">
          <i class="icon-slides"></i>
        </span>
        <div class="title">{{ $t('presentationTemplate') }}</div>
        <div class="text">{{ $t('presentationTemplateDetail') }}</div>
        <div class="presentation-container">
          <div class="button-container">
            <div class="button-title">{{ $t('frenchVersion') }}</div>
            <a class="download" href="/presentation/Commuter_Fr.pptx" download>
              {{ $t('download') }}
            </a>
          </div>

          <div class="button-container">
            <div class="button-title">{{ $t('englishVersion') }}</div>
            <a class="download" href="/presentation/Commuter_En.pptx" download>
              {{ $t('download') }}
            </a>
          </div>
        </div>
      </div>
      <FooterSection class="footer-section" />
    </div>

    <FooterSection v-else-if="isDesktop" class="footer-section footer-client" />
  </div>
</template>

<script>
import Card from '@/components/Study/Card';
import FooterSection from '@/components/Footer';
import Loader from '@/components/Loader';
import { mapGetters } from 'vuex';
import { useSignalR } from '@dreamonkey/vue-signalr';

export default {
  components: {
    Card,
    FooterSection,
    Loader,
  },

  data() {
    return {
      searchedStudies: [],
      searchedFullStudies: [],
      fetching: false,
      search: '',
      pageIndex: 0,
      pageSize: 10,
    };
  },

  computed: {
    ...mapGetters({
      isDesktop: 'isDesktop',
      userInfo: 'userInfo',
      studies: 'studies',
      isDuplicating: 'isDuplicating',
    }),
  },

  watch: {
    studies: {
      handler() {
        if (this.isDesktop) {
          this.searchStudies();
        }
      },

      deep: true,
    },

    search() {
      if (this.isDesktop) {
        this.searchStudies();
      }
    },
  },

  created() {
    this.$socket = useSignalR();
  },

  mounted() {
    if (!this.isDesktop) {
      this.$socket.invoke('action', { action: 'selectMenu', parameters: 'INTERSTICIEL' });
    }

    this.fetching = true;
    this.$store
      .dispatch('fetchStudies')
      .then(() => {
        this.fetching = false;
      })
      .catch(error => {
        console.error(error);
        this.fetching = false;
      });
  },

  beforeUnmount() {
    this.$store.dispatch('cancelLoading');
  },

  methods: {
    searchStudies() {
      if (this.search) {
        const resultList = this.studies.filter(
          study =>
            study.countries.filter(e => e.toLowerCase().includes(this.search.toLowerCase())).length ||
            study.name.toLowerCase().includes(this.search.toLowerCase()) ||
            study.clientName.toLowerCase().includes(this.search.toLowerCase()) ||
            `${study.user.firstname} ${study.user.lastname}`.toLowerCase().includes(this.search.toLowerCase()),
        );

        this.searchedStudies = this.isDesktop ? resultList : resultList.slice(0, 3);
        this.searchedFullStudies = resultList;
      } else {
        this.searchedFullStudies = this.studies;
        this.searchedStudies = this.isDesktop ? this.studies : [];
      }
    },

    showAllresults() {
      if (this.searchedFullStudies && this.searchedFullStudies.length > 0 && this.searchedStudies.length !== this.searchedFullStudies.length) {
        this.searchedStudies = this.searchedFullStudies;
      } else {
        this.search = '';
        this.searchedStudies = this.studies;
      }
    },

    hideAllresults() {
      this.searchedStudies = [];
      this.searchedFullStudies = [];
    },

    newStudy() {
      this.$store.dispatch('updateCurrentModal', { component: 'AdminFormCreate', params: null });
      // this.$router.push({ name: 'studyCreate' })
    },

    downloadCSV() {
      window.open(
        'https://commuterprodstorage2.blob.core.windows.net/publicresources/commuter_app.xlsx?sp=r&st=2024-11-13T10:30:54Z&se=2050-11-13T18:30:54Z&spr=https&sv=2022-11-02&sr=b&sig=Bn7qbPXgkTBhvS4n3gUIAeWURSKgqNVG32w2fUAPOok%3D',
        '_blank',
      );
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/styles/colors.scss';

.view-gabarit.admin {
  background-color: #f9f9f9;
  overflow: auto;
  overflow-x: hidden;

  .content {
    max-width: 1024px;
    width: 100%;
    margin: 0 auto;

    @media only screen and (max-width: 1024px) {
      padding: 0 24px;
      box-sizing: border-box;
    }
  }

  .header-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 48px 0 24px;

    .title {
      font-weight: 600;
      color: colors.$near-dark-color;
      font-size: 32px;
    }

    .search-bar {
      position: relative;
      width: 320px;
      height: 40px;
      display: flex;
      align-items: center;

      &.remote {
        width: 410px;
      }

      .icon-glass {
        position: absolute;
        top: 10px;
        bottom: 8px;
        left: 8px;
        width: 24px;
      }

      .search-input {
        width: 100%;
        height: 100%;
        padding: 4px 0 4px 40px;
        font-size: 14px;
        border: 1px solid #e4e1d8;
        border-radius: 2px;
        box-sizing: border-box;

        &::placeholder {
          color: #b8b6b0;
        }

        &:focus {
          outline: none;
          border-color: colors.$red-active-color;
        }
      }

      .search-button {
        height: 32px;
        line-height: 32px;
        font-size: 14px;
        background: colors.$red-active-color;
        color: colors.$white-color;
        border-radius: 48px;
        padding: 0 12px;
        margin: 0 0 0 8px;
        box-shadow: 0 0 2px rgba(0, 0, 0, 0);
        transition: all 0.3s ease;
        cursor: pointer;

        &:hover {
          background: colors.$red-active-color;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        }

        &:active {
          background: colors.$red-active-color;
          transform: scale(0.95);
        }
      }
    }

    .study-button {
      display: flex;
      align-items: center;
      height: 32px;
      font-size: 14px;
      background: colors.$red-active-color;
      color: colors.$white-color;
      border-radius: 48px;
      border: none;
      padding: 0 16px 0 0;
      box-shadow: 0 0 2px rgba(0, 0, 0, 0);
      transition: all 0.3s ease;

      .icon-sizer {
        width: 30px;
        height: 30px;
        margin: 0 0 0 2px;
        display: flex;
        justify-content: center;
        align-items: center;

        i {
          font-size: 30px;
        }
      }

      &:hover {
        background: colors.$red-active-color;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
      }

      &:active {
        transform: scale(0.95);
        background: colors.$red-active-color;
      }
    }
  }

  .study-counter {
    margin: -16px 0 16px;

    .show-all {
      transition: 0.2s;
      cursor: pointer;

      &:hover {
        text-decoration: underline;
        opacity: 0.8;
      }
    }
  }

  .study-list {
    text-align: center;
    min-height: calc(100vh - 182px);
    box-sizing: border-box;

    &:not(.has-footer) {
      padding-bottom: 64px !important;
    }

    &:not(.empty) {
      padding: 24px 0 0;
      background: colors.$white-color;
      box-shadow:
        0 1px 3px 0 rgba(0, 0, 0, 0.5),
        0 2px 10px 0 rgba(0, 0, 0, 0.1);
    }

    &.empty {
      min-height: calc(100vh - 162px);
    }

    &.has-footer {
      min-height: calc(100vh - 546px);
    }

    &.empty.has-footer {
      min-height: calc(100vh - 510px);
    }

    .no-study {
      padding: 10% 0 0;
    }

    .showall-button {
      height: 32px;
      line-height: 32px;
      width: 192px;
      font-size: 14px;
      background: colors.$red-active-color;
      color: colors.$white-color;
      border-radius: 48px;
      margin: 48px auto -32px;
      padding: 0;
      border: none;
      box-shadow: 0 0 2px rgba(0, 0, 0, 0);
      cursor: pointer;
      transition: all 0.3s ease;

      &:last-child {
        margin-bottom: 0;
      }

      &:hover {
        background: colors.$red-active-color;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
      }

      &:active {
        transform: scale(0.95);
        background: colors.$red-active-color;
      }
    }

    .hideall-button {
      margin: 48px auto 0;
      color: colors.$near-dark-color;
      text-decoration: underline;
      font-size: 14px;
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        opacity: 0.6;
      }
    }
  }

  .pagination {
    display: flex;
    padding: 24px 0;
    width: 100%;
    justify-content: center;

    background: colors.$white-color;
    user-select: none;

    .content {
      display: flex;
      max-width: 1024px;
      width: 100%;
      justify-content: flex-end;
    }

    .prev,
    .next,
    .number {
      padding: 0 8px;
      cursor: pointer;

      &:hover {
        color: rgba(colors.$red-color, 0.8);
      }

      &.active {
        font-weight: bold;
        color: colors.$red-color;
      }

      &.disabled {
        opacity: 0.4;
        pointer-events: none;
      }
    }
  }

  .footer {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: 48px 0;
    background: colors.$dark-color;
    border-bottom: solid 12px colors.$red-active-color;
    box-sizing: border-box;

    .horizontal-divider {
      width: 1px;
      height: 256px;
      margin: 0 48px;
      background: colors.$white-color;
      opacity: 0.3;
    }

    .footer-item {
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: center;
      width: 35%;
      height: 256px;

      .footer-icon {
        width: 48px;
        height: 48px;
        border-radius: 4px;
        display: flex;
        justify-content: center;
        align-items: center;

        i {
          font-size: 3.5em;
          color: colors.$white-color;
        }
      }

      .title {
        font-size: 22px;
        font-weight: bold;
        margin: 16px 0 8px;
        text-align: center;
        color: colors.$white-color;
      }

      .text {
        margin: 8px 0;
        height: 64px;
        text-align: center;
        color: colors.$beige-color;
        opacity: 0.7;
      }

      .presentation-container {
        display: flex;
        margin-top: -16px;

        .button-container {
          display: flex;
          flex-direction: column;
          justify-content: center;
          margin: -8px 12px 0;

          .button-title {
            text-align: center;
            color: white;
          }
        }
      }

      .download {
        display: flex;
        align-items: center;
        justify-content: center;

        width: 128px;
        height: 32px;
        padding: 0;
        margin: 16px auto 0;
        font-size: 14px;
        color: colors.$white-color;
        border: solid 2px colors.$white-color;
        background: none;
        border-radius: 32px;
        font-weight: bold;
        cursor: pointer;
        transition: all 0.3s ease;

        &a {
          margin-top: 0;
        }

        &:hover {
          color: colors.$dark-color;
          background: colors.$white-color;
        }
      }
    }

    .footer-section {
      position: absolute;
      bottom: 8px;
      color: colors.$beige-color;
      opacity: 0.7;
    }
  }

  .footer-section.footer-client {
    position: absolute;
    bottom: 0;
  }
}

form {
  margin: 5% auto;
  max-width: 460px;
  width: 100%;
  border: 4px solid #e3e3e3;
  border-radius: 20px;
  box-sizing: border-box;
  padding: 20px;
}

fieldset {
  border: none;
  box-shadow: none;
  position: relative;

  label {
    display: block;
    letter-spacing: 0.05em;
    margin-bottom: 4px;
    font-weight: 700;
  }

  input + label {
    display: inline-block;
  }

  input[type='text'] {
    font-family: inherit;
    padding: 0.75em 1em;
    line-height: 1;
    font-size: 12px;
    border-radius: 4px;
    border: 1px solid colors.$green-color;
    transition: all 0.3s ease;
    box-sizing: border-box;
    width: 100%;

    &.has-error {
      border-color: colors.$red-color;
    }

    & + input {
      margin-top: 1em;
    }
  }
}

.autocomplete-dropdown {
  background-color: colors.$white-color;
  box-shadow: 0 4px 8px rgba(colors.$black-color, 0.05);
}

.autocomplete-entry {
  font-size: 12px;
  padding: 0.75em 1em;

  &:hover {
    cursor: pointer;
    color: colors.$green-color;
  }
}
.loader-holder {
  position: fixed;
  z-index: 10;
  background-color: rgba(0, 0, 0, 0.15);
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  .loader-container {
    right: 50%;
    bottom: 50%;
    margin-right: -18px;
    margin-bottom: -18px;
  }
}
</style>
